/**
 * The learner's own rank explained (D-165): the score of the activity reports (D-161), shared by
 * the web and the mobile app. Everything comes from `rank` of `GET academy/progress/`: the
 * weights and the windows are the seed's (never written in the apps), the score and its three
 * parts are the learner's own (never another learner's).
 */

export const RANK_PARTS = ["level", "progress", "activity"] as const;
export type RankPart = (typeof RANK_PARTS)[number];

export interface RankFormula {
  /** Weights in % (they add up to 100). */
  weights: Record<RankPart, number>;
  lookbackDays: number;
  halfLifeDays: number;
  progressDays: number;
  activityDays: number;
}

export interface RankScore {
  /** Out of 100. */
  total: number;
  /** Each out of 100. */
  parts: Record<RankPart, number>;
  activeDays: number;
}

export interface RankExplained {
  formula: RankFormula | null;
  score: RankScore | null;
  /** The part where the most points can still be won. */
  focus: RankPart | null;
  computedAt: string | null;
}

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function num(value: unknown): number | null {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

function isPart(value: unknown): value is RankPart {
  return typeof value === "string" && (RANK_PARTS as readonly string[]).includes(value);
}

function parseFormula(value: unknown): RankFormula | null {
  if (!isObject(value) || !isObject(value.weights)) return null;
  const raw = value.weights;
  const weights = Object.fromEntries(RANK_PARTS.map((part) => [part, num(raw[part])]));
  if (RANK_PARTS.some((part) => weights[part] === null)) return null;
  return {
    weights: weights as Record<RankPart, number>,
    lookbackDays: num(value.lookback_days) ?? 0,
    halfLifeDays: num(value.half_life_days) ?? 0,
    progressDays: num(value.progress_days) ?? 0,
    activityDays: num(value.activity_days) ?? 0,
  };
}

function parseScore(value: unknown): RankScore | null {
  if (!isObject(value)) return null;
  const total = num(value.total);
  const parts = { level: num(value.level), progress: num(value.progress), activity: num(value.activity) };
  if (total === null || RANK_PARTS.some((part) => parts[part] === null)) return null;
  return { total, parts: parts as Record<RankPart, number>, activeDays: num(value.active_days) ?? 0 };
}

/** The part to work on: the server's choice, else the one with the most points still to win. */
export function weakestPart(score: RankScore | null, formula: RankFormula | null): RankPart | null {
  if (!score || !formula) return null;
  let best: RankPart | null = null;
  let missing = -1;
  for (const part of RANK_PARTS) {
    const lost = formula.weights[part] * (100 - score.parts[part]);
    if (lost > missing) {
      missing = lost;
      best = part;
    }
  }
  return best;
}

/** `rank` of the progress response (TCF, TEF, IELTS, or one Goethe level). */
export function parseRankExplained(rank: unknown): RankExplained {
  const data = isObject(rank) ? rank : {};
  const formula = parseFormula(data.formula);
  const score = parseScore(data.score);
  return {
    formula,
    score,
    focus: isPart(data.focus) ? data.focus : weakestPart(score, formula),
    computedAt: typeof data.computed_at === "string" ? data.computed_at : null,
  };
}

/** A score out of 100, rounded to the unit (« 72 »). */
export function formatRankPoints(value: number): string {
  return String(Math.round(value));
}
